import type { HttpTransport } from '@mangovoice/api-client'
import type { Call, CallLog, PaginatedApiResult } from '@mangovoice/types'
import type { components } from '@mangovoice/types/generated/mango-api'

import { stringifyParams } from './utils'

export interface CallsQueryParams {
  pbx_ids?: number[]
  limit?: number
  offset?: number
  ordering?: string[]
  search?: string
  /** ISO date-time string or Date (Dates are serialized via toISOString). */
  started_after?: string | Date
  started_before?: string | Date
  direction?: 'inbound' | 'outbound'
  answered?: boolean
  has_voicemail?: boolean
  has_transcript?: boolean
  [key: string]: unknown
}

export type CallTranscriptInstance =
  components['schemas']['CallTranscriptRetrieve']

export type FindCallTranscriptResult =
  components['schemas']['CallTranscriptList']

export interface MissedCallUpdatePayload {
  call_id: number
  follow_up_status: string
  follow_up_user: number | null
  follow_up_time_utc: string
  pbx: number
}

export interface CallsExportPayload {
  startDate: string
  endDate: string
  pbxIds: number[]
  direction?: 'inbound' | 'outbound'
  answered?: boolean
  hasVoicemail?: boolean
}

function stringifyCallParams(params: CallsQueryParams): string {
  return stringifyParams(params as Record<string, unknown>)
}

export function createCallsResource(transport: HttpTransport) {
  return {
    getCalls: (params: CallsQueryParams, config?: { signal?: AbortSignal }) => {
      const qs = stringifyCallParams(params)
      return transport.get<PaginatedApiResult<Call>>(
        `/calls/?${qs}`,
        undefined,
        config,
      )
    },

    getCall: (callId: number, config?: { signal?: AbortSignal }) =>
      transport.get<Call>(`/calls/${callId}/`, undefined, config),

    /** Get missed calls */
    getMissedCalls: (
      params: CallsQueryParams,
      config?: { signal?: AbortSignal },
    ) => {
      const qs = stringifyCallParams(params)
      return transport.get<PaginatedApiResult<Call>>(
        `/app/v1/missed-calls/?${qs}`,
        undefined,
        config,
      )
    },

    /** Update a missed call's follow-up status */
    updateMissedCall: (payload: MissedCallUpdatePayload) =>
      transport.post<Call>('/app/v1/missed-calls/', payload),

    /** Get recent call logs */
    getRecentCalls: (pbxId: string | number) =>
      transport.get<PaginatedApiResult<CallLog>>('/call-logs/', {
        pbx_id: pbxId,
        limit: 100,
      }),

    /** @deprecated Legacy call log endpoint */
    getCall_Legacy: (callId: number) =>
      transport.get<unknown>(`/call-logs/${callId}/`),

    /** Click to call */
    postClickToCall: (params: {
      extensionId: number
      phoneNumber: string
      autoAnswer: boolean
    }) =>
      transport.post<unknown>(
        `/app/v1/extensions/${params.extensionId}/click-to-call/`,
        {
          phone_number: params.phoneNumber,
          auto_answer: params.autoAnswer,
        } satisfies components['schemas']['ExtensionClickToCall'],
      ),

    /** Start a calls export job */
    startCallsExport: (filters: CallsExportPayload) =>
      transport.post<unknown>('/app/v1/reporting/calls/export/', { filters }),

    /** Get export job status */
    getCallsExportStatus: (jobId: string) =>
      transport.get<unknown>('/app/v1/reporting/calls/export/status/', {
        job_id: jobId,
      }),

    /** Get call transcripts list by call */
    findCallTranscriptByCallId: (pbxId: number, callId: number) => {
      const qs = stringifyParams({ pbx_id: pbxId, call_id: callId })
      return transport.get<PaginatedApiResult<FindCallTranscriptResult>>(
        `/call-transcripts/?${qs}`,
      )
    },

    /** Get a single call transcript */
    getCallTranscript: (transcriptId: number) =>
      transport.get<CallTranscriptInstance>(
        `/call-transcripts/${transcriptId}/`,
      ),

    /** List transcribed calls for a PBX */
    getTranscribedCallList: (pbxId: number, limit = 250) => {
      const qs = stringifyCallParams({
        pbx_ids: [pbxId],
        has_transcript: true,
        ordering: ['-started_at'],
        limit,
      })
      return transport.get<PaginatedApiResult<Call>>(`/calls/?${qs}`)
    },
  }
}
